/**
 * A stable, per-browser device id sent on every request as the `x-device-id`
 * header. The server binds it to the login session (RefreshToken.deviceId),
 * stamps it on web punches (AttendanceEvent.deviceFingerprint) feeding the
 * SHARED_DEVICE_MULTIPLE_USERS anti-buddy-punch heuristic, and — when the
 * device approval gate is on — decides whether this browser may sign in at all
 * (UserDevice).
 *
 * Deliberately a random UUID — NOT a canvas/hardware fingerprint. Hardware
 * fingerprints collide across identical company-issued phones (making the
 * shared-device signal useless, and, now that the same value gates sign-in,
 * letting one approved handset unlock a hundred others) and read as covert
 * tracking. A random id per browser profile is honest and exactly the
 * granularity both consumers need: "was this same browser used by two different
 * workers?".
 *
 * STORED IN TWO PLACES, COOKIE FIRST. Once losing the id means losing access
 * until an admin re-approves, a single storage location is too fragile: a
 * browser told to clear site data, an iOS profile evicting localStorage under
 * pressure, or a user "clearing the cache" to fix something unrelated would all
 * mint a fresh id and land the person back in the approval queue. The cookie is
 * read BEFORE localStorage because it is the one that survives the common
 * "clear cache" gestures, and whichever store still holds the id repopulates
 * the other. SameSite=Strict: this value is only ever sent by our own
 * first-party requests, so there is no cross-site flow to preserve.
 */
import { randomUuid } from './uuid.js';

const STORAGE_KEY = 'hadir.deviceId';
const COOKIE_KEY = 'hadir_device_id';
const COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

/** Only our own generated ids are honoured — a hand-edited cookie is ignored. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

let cached: string | null = null;

function readCookie(): string | null {
  try {
    const match = document.cookie
      .split(';')
      .map((c) => c.trim())
      .find((c) => c.startsWith(`${COOKIE_KEY}=`));
    if (!match) return null;
    const value = decodeURIComponent(match.slice(COOKIE_KEY.length + 1));
    return UUID_RE.test(value) ? value : null;
  } catch {
    return null;
  }
}

function writeCookie(id: string): void {
  try {
    // `Secure` only on HTTPS: the dev server is plain http://localhost and a
    // Secure cookie there is silently dropped, which would leave the fallback
    // permanently empty in development — the one place it needs to be
    // debuggable.
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie =
      `${COOKIE_KEY}=${encodeURIComponent(id)}; Max-Age=${COOKIE_MAX_AGE_SECONDS}; ` +
      `Path=/; SameSite=Strict${secure}`;
  } catch {
    // Cookies disabled — localStorage alone still works.
  }
}

function readLocalStorage(): string | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value && UUID_RE.test(value) ? value : null;
  } catch {
    return null;
  }
}

function writeLocalStorage(id: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // Private mode / storage disabled — the cookie alone still works.
  }
}

export function getDeviceId(): string {
  if (cached) return cached;

  // Cookie first: it is the store that survives "clear the cache", so reading
  // it first is what keeps an approved browser approved.
  const existing = readCookie() ?? readLocalStorage();
  const id = existing ?? randomUuid();

  // Always write BOTH, even when the id came from one of them: whichever store
  // was cleared is repopulated from the survivor, so the pair heals itself.
  writeCookie(id);
  writeLocalStorage(id);

  cached = id;
  return id;
}
